import { useLayoutEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import BetCard from './BetCard'

// Entrance timing for the newest row (index 0). The row grows from a collapsed
// (zero-height) box to its natural height — that height growth is what pushes
// the existing rows downward — while it simultaneously slides in from above and
// fades in. Mirrors the LastCrashMultipliers chip entrance, on the Y axis.
const ENTER_DURATION = 450
const ENTER_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const Transaction = ({ rows, page }) => {
  const { displayCurrencyCode } = useSelector((state) => state.wallet)

  const listRef = useRef(null)
  const prevHeadIdRef = useRef(null)
  const prevPageRef = useRef(null)
  const cleanupRef = useRef(null)

  // Stable identity of the top row, used to detect a freshly prepended bet.
  const headId = rows?.length ? rows[0]?.id : null

  useLayoutEffect(() => {
    const prevHeadId = prevHeadIdRef.current
    const prevPage = prevPageRef.current
    prevHeadIdRef.current = headId
    prevPageRef.current = page

    // Only animate a genuinely new transaction prepended to the first page:
    // skip the first paint (prevHeadId null), empty lists, unchanged heads, and
    // page navigation. Newest bets only ever appear at the top of page 1, and
    // requiring both this render and the previous one to be page 1 rules out the
    // head change that comes from paging *back* to page 1 from elsewhere.
    if (prevHeadId === null || headId === null || headId === prevHeadId) return
    if (page !== 1 || prevPage !== 1) return
    if (prefersReducedMotion()) return

    const container = listRef.current
    const el = container?.firstElementChild
    if (!el) return

    // Finalize any entrance still in flight (rapid settlements) before a new one.
    if (cleanupRef.current) cleanupRef.current()

    const styles = window.getComputedStyle(container)
    const gap = parseFloat(styles.rowGap || styles.gap || '0') || 0
    const naturalHeight = el.offsetHeight // border-box outer height at natural size

    // FIRST — collapse the newcomer so the list looks exactly as it did before
    // the update (no visible jump), anchored to its top edge.
    el.style.boxSizing = 'border-box'
    el.style.overflow = 'hidden'
    el.style.transformOrigin = 'top center'
    el.style.willChange = 'height, transform, opacity'
    el.style.transition = 'none'
    el.style.height = '0px'
    el.style.marginBottom = `-${gap}px` // cancel the flex gap while collapsed
    el.style.transform = 'translateY(-8px)'
    el.style.opacity = '0'

    // Commit the collapsed state before we animate to the final one.
    void el.offsetHeight // force reflow

    // PLAY — grow to full height (this slides every row below downward) and
    // slide in from above while fading in.
    el.style.transition =
      `height ${ENTER_DURATION}ms ${ENTER_EASING}, ` +
      `margin-bottom ${ENTER_DURATION}ms ${ENTER_EASING}, ` +
      `transform ${ENTER_DURATION}ms ${ENTER_EASING}, ` +
      `opacity ${Math.round(ENTER_DURATION * 0.6)}ms ease-out`
    el.style.height = `${naturalHeight}px`
    el.style.marginBottom = '0px'
    el.style.transform = 'translateY(0)'
    el.style.opacity = '1'

    let done = false
    const finish = () => {
      if (done) return
      done = true
      el.removeEventListener('transitionend', onEnd)
      clearTimeout(timer)
      // Return to the natural, CSS-driven layout.
      el.style.transition = ''
      el.style.height = ''
      el.style.marginBottom = ''
      el.style.transform = ''
      el.style.opacity = ''
      el.style.boxSizing = ''
      el.style.overflow = ''
      el.style.transformOrigin = ''
      el.style.willChange = ''
      if (cleanupRef.current === finish) cleanupRef.current = null
    }
    const onEnd = (e) => {
      if (e.target === el && e.propertyName === 'height') finish()
    }
    el.addEventListener('transitionend', onEnd)
    const timer = setTimeout(finish, ENTER_DURATION + 120) // safety net
    cleanupRef.current = finish

    return finish
  }, [headId, page])

  const HEADER_ELEMENTS = [
    {
      label: 'Date',
      currency: false,
    },
    {
      label: 'Type',
      currency: false,
    },
    {
      label: 'Stake',
      currency: true,
    },
    {
      label: 'X',
      currency: false,
    },
    {
      label: 'Payout',
      currency: true,
    },
  ]

  return (
    <div className="standard-normal-transaction-outer-container">
      <div className="standard-normal-transaction-header-container">
        {HEADER_ELEMENTS.map((element, index) => (
          <div key={index} className="standard-normal-transaction-header-element-container">
            <div className="standard-normal-transaction-header-element">{element.label}</div>
            {element.currency && (
              <div className="standard-normal-transaction-header-element-currency">
                [ {displayCurrencyCode} ]
              </div>
            )}
          </div>
        ))}
      </div>
      <div ref={listRef} className="standard-normal-transaction-inner-container no-scrollbar">
        {rows?.map((transaction, index) => {
          if (transaction.type === 'BET') {
            return <BetCard key={transaction.id ?? index} data={transaction} />
          }
        })}
      </div>
    </div>
  )
}

export default Transaction
